import React, { useState, useEffect } from "react";
import adminClasses from "../../../landing-pages/admin/admin.module.css";
import adminCandidateService from "../../../../services/admin/admin-candidate-service";
import toasterUtils from "../../../../utils/toaster-utils";
import RegisteredCandidatesGridComponent from "./RegisteredCandidatesGridComponent";
import RegisteredCandidatesSearchInputModel from "../../../../models/admin/candidate/request/RegisteredCandidatesSearchInputModel";
import RegisteredCandidatesModel from "../../../../models/admin/candidate/response/RegisteredCandidatesModel";
import { SortDirection } from "../../../../models/enums/SortDirectionEnum";
import IdNameModel from "../../../../models/common/IdNameModel";
import candidateService from "../../../../services/candidate/candidate-service";
import { FileUploadTypeEnum } from "../../../../models/enums/FileUploadTypeEnum";
import fileUtils from "../../../../utils/file-utils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import ScheduleInterviewPopUp from "../../../candidate/interview/ScheduleInterviewComponent";
import { GeneralPoupConfig, ModalSize } from "../../../../models/modal-types";
import modalUtils from "../../../../utils/modal-utils";
import PagedListModel from "../../../../models/common/PagedListModel";
import { PagingEnum } from "../../../../models/enums/IAIEnum";
import masterDataService from "../../../../services/master-data-service";
import { FormControl, InputLabel, MenuItem, Select, Button, Autocomplete, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import dateTimeUtils from "../../../../utils/date-utils";

interface RegisteredCandidatesProps {
    primarySkillsList: IdNameModel[];
    timeSlotsList: IdNameModel[];
};

const RegisteredCandidatesComponent = (props: RegisteredCandidatesProps) => {
    const [candidatePagedList, setCandidatePagedList] = useState<PagedListModel<RegisteredCandidatesModel>>();
    const [candidateList, setCandidateList] = useState<RegisteredCandidatesModel[]>([]);
    const [refreshCandidate, setRefreshCandidate] = useState<number>(2);
    const [startDateSearch, setStartDateSearch] = useState<Dayjs | null>(dayjs());
    const [endDateSearch, setEndDateSearch] = useState<Dayjs | null>(dayjs());
    const [primarySKillSearch, setPrimarySkillSearch] = useState<number>(0);
    const [nameFilter, setNameFilter] = useState<string>("");
    const [emailIdFilter, setEmailIdFilter] = useState<string>("");
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("CandidateName");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirection);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);

    useEffect(() => {
        loadPageSizes();
        clearSearch();
    }, []);

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (nameFilter: string, emailIdFilter: string, page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: RegisteredCandidatesSearchInputModel = {
            startDate: dateTimeUtils.getFormattedDate(startDateSearch ? startDateSearch.toDate() : new Date()),
            endDate: dateTimeUtils.getFormattedDate(endDateSearch ? endDateSearch.toDate() : new Date()),
            primarySkillId: primarySKillSearch,
            name: nameFilter,
            emailId: emailIdFilter,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getCandidates = (inputData: RegisteredCandidatesSearchInputModel) => {
        setRefreshCandidate(2);
        modalUtils.showLoader();
        adminCandidateService.getRegisteredCandidates(inputData).then((res) => {
            modalUtils.closeModal();
            setCandidateList([]);
            setCandidatePagedList(res);
            setCandidateList(res.items);
            setRefreshCandidate(refreshCandidate * 5);
        }).catch((error) => {
            setRefreshCandidate(refreshCandidate * 5);
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const clearSearch = () => {
        setCandidateList([]);
        setStartDateSearch(dayjs());
        setEndDateSearch(dayjs());
        setPrimarySkillSearch(0);
        setRefreshCandidate(2);
    };

    const onDownloadResumeActionHandler = (candidateId: string, candidateName: string) => {
        modalUtils.showLoader();
        candidateService.downloadCandidateResume(candidateId, FileUploadTypeEnum.CandidateResume.toString()).then((res) => {
            modalUtils.closeModal();
            fileUtils.downloadFile(res, candidateName);
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onScheduleActionHandler = (candidateId: string) => {
        const config: GeneralPoupConfig = {
            title: "Add User",
            content: (
                <ScheduleInterviewPopUp
                    primarySkillsList={props.primarySkillsList}
                    timeSlotsList={props.timeSlotsList}
                    candidateId={candidateId}
                />
            ),
            size: ModalSize.XL,
            // className: "model-min-height-500"
        };
        modalUtils.showPopup(config);
    };

    const onVerifyActionHandler = (candidateId: string) => {
        modalUtils.showLoader();
        adminCandidateService.verifyCandidate(candidateId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            getCandidates(getSearchFilters(nameFilter, emailIdFilter, page, pageSize, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onSearchClickHandler = () => {
        setPage(0);
        setNameFilter("");
        setEmailIdFilter("");
        setSortColumn("CandidateName");
        setSortDirection(SortDirection.Asc);
        getCandidates(getSearchFilters("", "", 0, pageSize, "CandidateName", SortDirection.Asc))
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
        getCandidates(getSearchFilters(nameFilter, emailIdFilter, newPage, pageSize, sortColumn, sortDirection));
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
        getCandidates(getSearchFilters(nameFilter, emailIdFilter, PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    const onSortActionHandler = (newSortColumn: string, sortDir: string) => {
        setSortColumn(newSortColumn);
        setSortDirection(sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc);
        getCandidates(getSearchFilters(nameFilter, emailIdFilter, page, pageSize, newSortColumn, sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc));
    };

    const onFilterActionHandler = (filterColumn: string, filterValue: string) => {
        setPage(0);
        if (filterColumn === "Name") {
            setNameFilter(filterValue);
            getCandidates(getSearchFilters(filterValue, emailIdFilter, 0, pageSize, sortColumn, sortDirection));
        } else {
            setEmailIdFilter(filterValue);
            getCandidates(getSearchFilters(nameFilter, filterValue, 0, pageSize, sortColumn, sortDirection));
        }
    };

    return (
        <div className="card card-white">
            <div className="card-body">
                <div className="row">
                    <div className="col-md-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Start Date"
                                value={startDateSearch}
                                onChange={(newValue) => setStartDateSearch(newValue)}
                                format="YYYY-MMM-DD"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        margin: "normal",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className="col-md-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="End Date"
                                value={endDateSearch}
                                onChange={(newValue) => setEndDateSearch(newValue)}
                                format="YYYY-MMM-DD"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        margin: "normal",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className="col-md-3">
                        <FormControl fullWidth margin="none" size="small">
                            <Autocomplete options={props.primarySkillsList} value={props.primarySkillsList.find((x) => x.id === primarySKillSearch) || null}
                                onChange={(_, newValue) => setPrimarySkillSearch(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Primary Skill" size="small" margin="normal" fullWidth />
                                )}
                            />
                        </FormControl>
                    </div>
                    <div className="col-md-3">
                        <Button variant="contained" color="success" onClick={() => onSearchClickHandler()} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                            Search
                        </Button>
                        <Button variant="contained" color="warning" onClick={() => clearSearch()} startIcon={<ClearIcon />} sx={{ ml: 2, mt: 2 }}>
                            Clear
                        </Button>
                    </div>
                </div>
                {candidateList.length > 0 && refreshCandidate > 2 &&
                    <RegisteredCandidatesGridComponent
                        key={refreshCandidate}
                        candidateList={candidateList}
                        totalSize={candidatePagedList?.totalCount ? candidatePagedList?.totalCount : candidateList.length}
                        currentPage={page + 1}
                        pagesize={pageSize}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                        pageSizesList={pageSizesList}
                        nameFilterValue={nameFilter}
                        emailIdFilterValue={emailIdFilter}
                        onDownloadResumeAction={onDownloadResumeActionHandler}
                        onScheduleAction={onScheduleActionHandler}
                        onVerifyAction={onVerifyActionHandler}
                        onPageChangeAction={onPageChangeActionHandler}
                        onPageSizeChangeAction={onPageSizeChangeActionHandler}
                        onSortAction={onSortActionHandler}
                        onFilterAction={onFilterActionHandler} />
                }
            </div>
        </div>
    );
};
export default RegisteredCandidatesComponent;